import {
  FC, useEffect, useState,
} from 'react';
import {
  Typography, Row, Col, Layout, Spin, message, Switch,
} from 'taxaroo-ui';

import { ApolloError } from '@apollo/client';
import { useAppSelector } from '~src/redux/hooks';
import { GetFirmSettingsDocument, useGetFirmSettingsQuery } from '~src/graphql/queries/settings';
import {
  useCreateFirmSettingMutation,
  useUpdateFirmSettingMutation,
} from '~src/graphql/mutations/settings';
import { FirmSettingsEntity, StripeSubscriptionStatus, TaxarooAiMessageType } from '~src/graphql';
import { useGetFirmAccountAddOnByNameQuery } from '~src/graphql/queries/addOns';
import CheckMark from 'src/assets/icons/checkmark.svg';
import { useCreateAiAgentMessageMutation } from '~src/graphql/mutations/messaging';
import { GetAiAgentMessagesDocument } from '~src/graphql/queries/messaging';
import SettingsHeader from '../SettingsHeader';
import { AiAssistantPlanType } from '../../AIAssistant';
import * as styles from '../../AIAssistant/styles.module.css';
import AddonsModalContent from '../../AIAssistant/AddonsModalContent';

const { Paragraph, Text, Title } = Typography;

export const chatOnlyCardContent = (
  <>
    <Row className={styles.modalCardTitle}>Chat only plan</Row>
    <Row style={{ fontSize: '14px' }}>Basic plan but only chat available</Row>

    <Row className={styles.modalCardFeatures}>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesBasicCheck} />
        Available from your firm dashboard
      </Row>
    </Row>
  </>
);

export const basicCardContent = (
  <>
    <Row className={styles.modalCardTitle}>Basic plan</Row>
    <Row style={{ fontSize: '14px' }}>The Starter Boost</Row>

    <Row className={styles.modalCardFeatures}>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesBasicCheck} />
        Limited to 1 hour session per day
      </Row>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesBasicCheck} />
        Available from your firm dashboard
      </Row>
      {/*
        <Row className={styles.modalCardFeaturesLine} align="middle">
          <CheckMark className={styles.modalCardFeaturesBasicCheck} />
          Message history access up to 15 days
        </Row>
        */}
    </Row>
  </>
);

export const premiumCardContent = (
  <>
    <Row className={[styles.modalCardTitle, styles.blueText]}>Premium plan</Row>
    <Row style={{ fontSize: '14px' }}>All-Inclusive Excellence</Row>

    <Row className={styles.modalCardFeatures}>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesPremiumCheck} />
        Limited to 2 hour session per day
      </Row>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesPremiumCheck} />
        Available from your firm dashboard
      </Row>
      <Row className={styles.modalCardFeaturesLine} align="middle">
        <CheckMark className={styles.modalCardFeaturesPremiumCheck} />
        Available from your client portal
      </Row>
      {/*
        <Row className={styles.modalCardFeaturesLine} align="middle">
          <CheckMark className={styles.modalCardFeaturesPremiumCheck} />
          Unlimited access to old messages
        </Row>
        */}
    </Row>
  </>
);

const Ai: FC = () => {
  const { firmAccountId } = useAppSelector((state) => state.session);
  const [enableClientBot, setEnableClientBot] = useState<boolean>(false);
  const [plan, setPlan] = useState<AiAssistantPlanType>();
  const [enableClientAiBotSetting, setEnableClientAiBotSetting] = useState<FirmSettingsEntity | null>(null);

  const [createAiAgentMessage] = useCreateAiAgentMessageMutation({
    refetchQueries: [GetAiAgentMessagesDocument],
    awaitRefetchQueries: true,
    onError: (err: ApolloError) => message.error(err.message),
  });

  const handleOnError = (error: ApolloError) => {
    message.error(`An error has occurred: ${error.message}.`);
  };

  const {
    data: aiSettingsData,
    loading: aiSettingsLoading,
    error: aiSettingsError,
  } = useGetFirmSettingsQuery({
    variables: { category: 'FIRM_AI_SETTINGS' },
    skip: !firmAccountId,
  });

  const { data: firmAccountPremiumAddOn } = useGetFirmAccountAddOnByNameQuery({
    variables: { name: 'taxaroo-ai-premium-plan' },
  });
  const [createFirmSetting, { loading: createFirmSettingLoading }] = useCreateFirmSettingMutation({
    onError: handleOnError,
    refetchQueries: [
      { query: GetFirmSettingsDocument, variables: { category: 'FIRM_AI_SETTINGS' } },
    ],
    awaitRefetchQueries: true,
  });
  const [updateFirmSetting, { loading: updateFirmSettingLoading }] = useUpdateFirmSettingMutation({
    onError: handleOnError,
    refetchQueries: [
      { query: GetFirmSettingsDocument, variables: { category: 'FIRM_AI_SETTINGS' } },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (aiSettingsData?.getFirmSettings) {
      const myEnableClientAiBotSetting = aiSettingsData.getFirmSettings.find(
        (setting) => setting.name === 'enable-client-ai-bot',
      );
      setEnableClientAiBotSetting(myEnableClientAiBotSetting);
      // todo
      setEnableClientBot(
        // eslint-disable-next-line no-underscore-dangle
        myEnableClientAiBotSetting?.value.__typename === 'BooleanValue'
          ? myEnableClientAiBotSetting.value.boolValue
          : false,
      );
    }
  }, [aiSettingsData?.getFirmSettings]);

  useEffect(() => {
    if (aiSettingsError) {
      return handleOnError(aiSettingsError);
    }
    return null;
  }, [aiSettingsError]);

  const changeEnableAiInClientPortal = async (val: boolean) => {
    setEnableClientBot(val);
    try {
      if (enableClientAiBotSetting?.id) {
        await updateFirmSetting({
          variables: {
            id: enableClientAiBotSetting.id,
            value: String(val),
          },
        });
      } else {
        await createFirmSetting({
          variables: {
            name: 'enable-client-ai-bot',
            value: String(val),
          },
        });
      }
    } catch (error) {
      setEnableClientBot(!val);
    }
  };

  const subscriptionCreated = async (newPlan: AiAssistantPlanType) => {
    message.success(`Subscription created for ${newPlan} plan`);

    await createAiAgentMessage({
      variables: {
        createAiAgentMessageInput: {
          type: TaxarooAiMessageType.System,
          text: `Welcome back! '${newPlan}' plan unlocked`,
        },
      },
    });

    // reload the page, todo: fix this
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  return (
    <Layout style={{ backgroundColor: 'white' }}>
      <SettingsHeader
        title="AI Settings"
        tooltipTitle="AI Settings"
        tooltipContent={(
          <Paragraph>
            <Text>Customize your AI settings</Text>
          </Paragraph>
        )}
      />
      <div style={{ paddingTop: '20px', marginBottom: '125px' }}>
        <Row style={{ width: '100%' }}>
          <Title level={5}>Enable AI Assistant in your client portal?</Title>
          <Text
            style={{
              backgroundImage: 'linear-gradient(to right, #0057A5, #0A8CED)',
              color: 'transparent',
              backgroundClip: 'text',
              marginLeft: '8px',
              lineHeight: '14px',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            (Coming Soon)
          </Text>
        </Row>

        {aiSettingsLoading && (
          <Row align="middle" justify="center">
            <Spin size="large" />
          </Row>
        )}

        <Row gutter={15} align="middle">
          <Col>
            <Switch
              onChange={(val: boolean) => changeEnableAiInClientPortal(val)}
              checked={enableClientBot}
              loading={updateFirmSettingLoading || createFirmSettingLoading}
              disabled={
                !firmAccountPremiumAddOn?.firmAccountAddOnByName?.id
                || firmAccountPremiumAddOn?.firmAccountAddOnByName?.subscriptionStatus
                  !== StripeSubscriptionStatus.Active
              }
            />
          </Col>
        </Row>
        <Row style={{ marginTop: '25px' }}>
          <AddonsModalContent
            subscriptionCreated={() => subscriptionCreated(plan)}
            subscriptionCancelled={() => {
              message.success('Subscription cancelled');
            }}
            plan={plan}
            setPlan={setPlan}
          />
        </Row>
      </div>
    </Layout>
  );
};

export default Ai;
